import {
    ActivatedRouteSnapshot,
    ResolveFn,
    Route,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { UserPositionDto, UserPositionFacade } from '@pf/platform-core/domain';
import { ModalService } from '@pf/shared-ui';
import { inject } from '@angular/core';
import { filter, first, map, Observable, of, switchMap } from 'rxjs';

import { CpwUserPositionTypes } from '@cpw/domain';
import { LoggerService } from '@pf/shared-services';

export type UserPositionResolverData = {
    positionType: string;
};

export function withUserPositionResolver(
    route: Route,
    positionType: CpwUserPositionTypes
): Route {
    return {
        ...route,
        data: {
            ...(route.data || {}),
            positionType,
        },
        resolve: {
            userPosition: userPositionResolver,
        },
    };
}

export const userPositionResolver: ResolveFn<UserPositionDto | null> = (
    route: ActivatedRouteSnapshot,
    _: RouterStateSnapshot
): Observable<UserPositionDto | null> => {
    const modalService = inject(ModalService);

    const newPosition: string = route.data['positionType'];

    if (!newPosition) {
        inject(LoggerService).error('No position type found in route data');
        return of(null);
    }

    const facade = inject(UserPositionFacade);

    return facade.loadingActive$.pipe(
        filter(loading => !loading),
        first(),
        map(() => facade.active),
        switchMap((active?: UserPositionDto | null) => {
            if (active?.userPositionTypeName === newPosition) {
                // already in the desired position
                return of(active as UserPositionDto);
            }

            return (
                !active?.userPositionTypeId
                    ? of(true)
                    : modalService.confirmAction$({
                          title: 'Confirm Position Exit',
                          content: `Are you sure you want to exit your ${active.userPositionTypeName || 'current'} position?`,
                          okText: 'Yes',
                          onOk: () => of(true),
                          onCancel: () => of(false),
                      })
            ).pipe(
                switchMap(confirmed => {
                    if (!confirmed) {
                        const router = inject(Router);
                        router.navigateByUrl('/');
                        return of(null);
                    }

                    return facade.setUserPosition$(newPosition);
                })
            );
        })
    );
};
